import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DEAD_PART_REASON_CODES, hasCdkDesc } from '../api/checks';
import { fetchItemFreshness, fetchProductDetail } from '../api/products';
import type { ItemFreshness, ProductDetail } from '../api/products';
import { REASON_LABELS, fetchItemAvailability } from '../api/home_value_checks';
import type { ItemAvailabilityPanel } from '../api/home_value_checks';
import { LocationChips, TransitionBadge } from '../components/data-table/homeValueColumns';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDateTime, fmtPrice, fmtPct } from '../utils/format';
import { CheckStatusBadge, ReasonBadge, ReviewStatusBadge } from '../components/badges';
import FreshnessAlert from '../components/item/FreshnessAlert';
import FreshnessValue from '../components/item/FreshnessValue';

// Grouped layout — order within group, group order on page.
// Any column not listed here lands in the "Other" group at the bottom,
// so the page auto-grows if tbnsdata gains new columns.
const GROUPS: Array<{ title: string; fields: string[] }> = [
  {
    title: 'Identifiers',
    fields: ['ns_product_id', 'sku', 'name', 'display_name', 'old_part_number', 'magento_id'],
  },
  {
    title: 'Vendor & manufacturer',
    fields: ['vendor', 'raw_vendor', 'ns_vendor', 'manufacturer', 'manufacturer_country', 'made_in_usa', 'cost_source'],
  },
  {
    title: 'Pricing',
    fields: ['purchase_price', 'price_confirmed', 'website_price', 'special_price', 'special_price_end_date', 'pricing_factor'],
  },
  {
    title: 'Core charge',
    fields: ['is_core_charge', 'core_charge'],
  },
  {
    title: 'Dimensions',
    fields: ['weight', 'height', 'width', 'length'],
  },
  {
    title: 'Inventory & status',
    fields: ['on_hand', 'home_value', 'home_value_confirmed', 'item_status', 'website_visible', 'inactive', 'returnable_status'],
  },
  {
    title: 'Other',
    fields: ['warranty', 'product_type'],
  },
];

const LABELS: Record<string, string> = {
  ns_product_id:        'NetSuite ID',
  sku:                  'SKU (Vendor Part Number)',
  name:                 'Item Name/Number',
  display_name:         'Display name',
  old_part_number:      'Old part number',
  magento_id:           'Magento ID',
  vendor:               'Vendor (code)',
  raw_vendor:           'Preferred vendor',
  ns_vendor:            'NS vendor',
  manufacturer:         'Manufacturer',
  manufacturer_country: 'Manufacturer country',
  made_in_usa:          'Made in USA',
  cost_source:          'Cost source',
  purchase_price:       'Purchase cost',
  website_price:        'Website price',
  special_price:        'Special price',
  special_price_end_date: 'Special price end date',
  pricing_factor:       'Pricing factor',
  is_core_charge:       'Core charge?',
  core_charge:          'Core charge amount',
  weight:               'Weight',
  height:               'Height',
  width:                'Width',
  length:               'Length',
  on_hand:              'On hand',
  item_status:          'Item status',
  website_visible:      'Website visible',
  inactive:             'Inactive',
  returnable_status:    'Returnable status',
  warranty:             'Warranty',
  product_type:         'Product type',
  home_value:           'Home Value',
  price_confirmed:      'Price confirmed',
  home_value_confirmed: 'Home Value confirmed',
};

// Columns that look like prices — render as $X.XX, treat -1000000 sentinel as "—".
const PRICE_FIELDS = new Set(['purchase_price', 'website_price', 'special_price', 'core_charge']);

// BOT-484: virtual rows (not tbnsdata columns), filled from GET /products/{id}/freshness.
// decision: freshness — see decisions/freshness.md
const FRESHNESS_FIELDS: Record<string, 'price' | 'home_value'> = {
  price_confirmed: 'price',
  home_value_confirmed: 'home_value',
};

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct]   = useState<ProductDetail | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState<string | null>(null);
  // Availability is a second, independent fetch: a failure there must not blank
  // the whole item page, so it has its own state and no error surface beyond an
  // empty panel.
  const [availability, setAvailability] = useState<ItemAvailabilityPanel | null>(null);
  // Freshness is a third independent fetch (BOT-484): a failure leaves "—" in the
  // two rows and no alert, never a blank page.
  const [freshness, setFreshness] = useState<ItemFreshness | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchProductDetail(Number(id))
      .then(setProduct)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchItemAvailability(id).then(setAvailability).catch(() => setAvailability(null));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setFreshness(null);
    // An older item's slow response must not overwrite the current item's freshness.
    let active = true;
    fetchItemFreshness(Number(id))
      .then(f => { if (active) setFreshness(f); })
      .catch(() => { if (active) setFreshness(null); });
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <p className="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>;
  }

  if (error || !product) {
    return (
      <div>
        <Link to="/all-items" className="text-sm text-blue-600 hover:text-blue-800">
          ← Back to products
        </Link>
        <p className="mt-6 text-center text-sm text-red-500">{error ?? 'Product not found'}</p>
      </div>
    );
  }

  // Bucket every column from the API response into a group, with leftovers in
  // "Other". price_check/history feed their own sections below, not the grid.
  const known = new Set([...GROUPS.flatMap(g => g.fields), 'price_check', 'history']);
  const leftovers = Object.keys(product).filter(k => !known.has(k));
  const priceCheck = product.price_check ?? null;
  const history = product.history ?? [];

  return (
    <div className="max-w-4xl pb-5">
      <Link to="/all-items" className="text-sm text-blue-600 hover:text-blue-800">
        ← Back to products
      </Link>

      <div className="mt-4 mb-6 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold text-gray-900">
          {(product.name as string) ?? '—'}
        </h1>
        <span className="font-mono text-sm text-gray-500">{(product.sku as string) ?? '—'}</span>
        {typeof product.vendor === 'string' && product.vendor && (
          <span className="rounded bg-blue-50 px-2 py-0.5 font-mono text-xs text-blue-700">
            {product.vendor}
          </span>
        )}
      </div>

      <div className="space-y-6">
        <FreshnessAlert alert={freshness?.alert} />

        {/* Fixed section set — always rendered; missing fields show "—" (MVP §6). */}
        {GROUPS.map(group => (
          <section
            key={group.title}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white"
          >
            <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              {group.title}
            </h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 sm:grid-cols-2">
              {group.fields.map(field =>
                FRESHNESS_FIELDS[field] ? (
                  <DetailRow key={field} label={LABELS[field]}>
                    <FreshnessValue value={freshness?.[FRESHNESS_FIELDS[field]]} />
                  </DetailRow>
                ) : (
                  <Row key={field} field={field} value={product[field]} />
                ),
              )}
            </dl>
          </section>
        ))}

        {/* Price Check — the item's latest greenlight check */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Price check
          </h2>
          {priceCheck ? (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 sm:grid-cols-2">
              <DetailRow label="Type">
                <span className={`rounded px-1.5 py-0.5 text-xs font-medium uppercase ${
                  priceCheck.status === 'unsafe' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                }`}>
                  {priceCheck.status}
                </span>
              </DetailRow>
              <DetailRow label="Reason code">
                {priceCheck.reason_code ?? '—'}
                {priceCheck.secondary_reason_code && ` + ${priceCheck.secondary_reason_code}`}
              </DetailRow>
              {/* BOT-384: CDK supersession — replacement part number from the
                  CDK file's PNC column (pacd/cupa only). */}
              {priceCheck.cdk_pnc && priceCheck.reason_code?.startsWith('CDK_') && (
                <DetailRow label="Superseded by (CDK)">
                  <span className="font-mono">{priceCheck.cdk_pnc}</span>
                </DetailRow>
              )}
              {/* CDK DESC — the exception text on coded parts ("CAN USE ...",
                  "NO REPLACEMENT"), plain part name otherwise. pacd/cupa only:
                  the underlying payload field exists for every vendor but only
                  carries the CDK column for these two.
                  decision: cdkdesc — see decisions/cdkdesc.md */}
              {hasCdkDesc(priceCheck) && (
                <DetailRow label="CDK description">{priceCheck.cdk_desc}</DetailRow>
              )}
              {/* BOT-377: replacement named by the vendor portal page itself
                  (fref "Replaced by: X"). Not gated on the reason code —
                  IS_DISCONTINUED outranks IS_SUPERSEDED, so a part that is both
                  would otherwise hide its successor. */}
              {priceCheck.vendor_supersedure && (
                <DetailRow label="Superseded by (vendor)">
                  <span className="font-mono">{priceCheck.vendor_supersedure}</span>
                </DetailRow>
              )}
              {/* Reman counterpart — a different relation from supersession, so
                  its own row, and only on a dead part. */}
              {priceCheck.vendor_reman_code &&
                DEAD_PART_REASON_CODES.has(priceCheck.reason_code ?? '') && (
                  <DetailRow label="Reman counterpart">
                    <span className="font-mono">{priceCheck.vendor_reman_code}</span>
                  </DetailRow>
                )}
              {/* BOT-384: live Paccar Parts Online status — RED code + the
                  ADDITIONAL DESCRIPTION text, refreshed nightly by
                  hf-paccar-portal-sync. */}
              {(priceCheck.portal_dis_code || priceCheck.portal_text || priceCheck.portal_supersedure) && (
                <DetailRow label="Paccar portal">
                  <div className="flex flex-col gap-0.5">
                    {priceCheck.portal_dis_code && (
                      <span className="font-mono text-xs">{priceCheck.portal_dis_code}</span>
                    )}
                    {priceCheck.portal_text && <span>{priceCheck.portal_text}</span>}
                    {priceCheck.portal_supersedure && (
                      <span className="font-mono text-xs" title="Replacement per Paccar Parts Online (Exception)">
                        → {priceCheck.portal_supersedure}
                      </span>
                    )}
                    {priceCheck.portal_checked_at && (
                      <span className="text-[10px] text-gray-400">
                        checked {new Date(priceCheck.portal_checked_at).toLocaleDateString('en-GB')}
                      </span>
                    )}
                  </div>
                </DetailRow>
              )}
              <DetailRow label="Delta %">{fmtPct(priceCheck.pct_diff)}</DetailRow>
              <DetailRow label="Vendor cost">
                <div className="flex flex-col leading-tight">
                  <span>{fmtPrice(priceCheck.vendor_price)}</span>
                  {priceCheck.core_charge && priceCheck.core_charge_amount != null && (
                    <span className="text-[10px] text-amber-700">
                      {fmtPrice(priceCheck.core_charge_amount)} core
                    </span>
                  )}
                </div>
              </DetailRow>
              <DetailRow label="NS cost (at check)">
                <div className="flex flex-col leading-tight">
                  <span>{fmtPrice(priceCheck.our_price)}</span>
                  {priceCheck.ns_core_charge != null && (
                    <span className="text-[10px] text-amber-700">
                      {fmtPrice(priceCheck.ns_core_charge)} core
                    </span>
                  )}
                </div>
              </DetailRow>
              <DetailRow label="Check read on">
                {fmtDateTime(priceCheck.price_date ?? priceCheck.created_at)}
              </DetailRow>
              <DetailRow label="Review status">{priceCheck.review_status}</DetailRow>
              <DetailRow label="Reviewed by">{priceCheck.reviewer_email ?? '—'}</DetailRow>
            </dl>
          ) : (
            <p className="px-4 py-4 text-sm text-gray-400">No active price check</p>
          )}
        </section>

        {/* Vendor availability (BOT-411) — live per-PDC stock, its observation
            trail, and the Home Value proposals derived from it. */}
        <AvailabilitySection panel={availability} />

        {/* Scraper screenshot */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Scraper screenshot
          </h2>
          <div className="px-4 py-4 text-sm">
            {priceCheck?.screenshot_url ? (
              <a
                href={priceCheck.screenshot_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                View screenshot →
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        </section>

        {/* Change history */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Change history
          </h2>
          {history.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-400">No history</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-gray-400">
                  <th className="px-4 py-2 font-medium">Price date</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium text-right">NS cost → Vendor cost</th>
                  <th className="px-4 py-2 font-medium text-right">Δ%</th>
                  <th className="px-4 py-2 font-medium">Outcome</th>
                  {/* Sync timestamp, not just the day — the review asked for the
                      hour so a run's pushes can be told apart (BOT-392). */}
                  <th className="px-4 py-2 font-medium">Synced at</th>
                  <th className="px-4 py-2 font-medium">Reviewed by</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {fmtDateTime(h.price_date ?? h.created_at)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col items-start gap-1">
                        <ReasonBadge code={h.reason_code} />
                        {h.secondary_reason_code && <ReasonBadge code={h.secondary_reason_code} />}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <CheckStatusBadge status={h.status} />
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                      {fmtPrice(h.our_price)} → {fmtPrice(h.vendor_price)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{fmtPct(h.pct_diff)}</td>
                    <td className="px-4 py-2">
                      <ReviewStatusBadge status={h.review_status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">{fmtDateTime(h.ns_pushed_at)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{h.reviewer_email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {leftovers.length > 0 && (
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Additional attributes
            </h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 sm:grid-cols-2">
              {leftovers.map(field => (
                <Row key={field} field={field} value={product[field]} />
              ))}
            </dl>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Availability panel.
 *
 * Three things, in the order an operator reads them: what the vendor holds right
 * now, how that has moved, and what Greenlight proposed (or wrote) as a result.
 * The stock chips are the same component the Home Value queue uses, so the
 * numbers look identical on both screens.
 */
function AvailabilitySection({ panel }: { panel: ItemAvailabilityPanel | null }) {
  const current = panel?.availability ?? [];
  const history = panel?.history ?? [];
  const checks = panel?.checks ?? [];

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        Vendor availability
      </h2>

      {current.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-400">No vendor stock recorded</p>
      ) : (
        current.map(row => (
          <dl
            key={`${row.vendor_code}-${row.sku}`}
            className="grid grid-cols-1 gap-x-6 gap-y-2 border-b border-gray-50 px-4 py-3 last:border-0 sm:grid-cols-2"
          >
            <DetailRow label="Vendor">{row.vendor_name || row.vendor_code}</DetailRow>
            <DetailRow label="Observed">{fmtDateTime(row.observed_at)}</DetailRow>
            <DetailRow label="PDC total">
              {/* null, not 0: "the page gave us no readable number" is a
                  different fact from "the vendor confirmed zero stock", and only
                  the second one may drive a delivery promise. */}
              {row.has_data ? (row.pdc_total ?? '—') : 'no usable data'}
            </DetailRow>
            <DetailRow label="Dealer stock (not counted)">{row.dealer_total ?? '—'}</DetailRow>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-400">Locations</dt>
              <dd className="mt-1">
                <LocationChips locations={row.locations} />
              </dd>
            </div>
          </dl>
        ))
      )}

      {checks.length > 0 && (
        <>
          <h3 className="border-y border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Home Value proposals
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-400">
                <th className="px-4 py-2 font-medium">Observed</th>
                <th className="px-4 py-2 font-medium">Change</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 font-medium">Outcome</th>
                <th className="px-4 py-2 font-medium">Synced at</th>
                <th className="px-4 py-2 font-medium">Reviewed by</th>
              </tr>
            </thead>
            <tbody>
              {checks.map(c => (
                <tr key={c.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {fmtDateTime(c.availability_observed_at)}
                  </td>
                  <td className="px-4 py-2"><TransitionBadge check={c} /></td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {c.reason_code ? (REASON_LABELS[c.reason_code] ?? c.reason_code) : '—'}
                  </td>
                  <td className="px-4 py-2"><ReviewStatusBadge status={c.review_status} /></td>
                  <td className="px-4 py-2 text-xs text-gray-500">{fmtDateTime(c.ns_pushed_at)}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{c.reviewer_email ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {history.length > 0 && (
        <>
          <h3 className="border-y border-gray-100 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Stock history
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-400">
                <th className="px-4 py-2 font-medium">Observed</th>
                <th className="px-4 py-2 font-medium text-right">PDC total</th>
                <th className="px-4 py-2 font-medium text-right">Dealer</th>
                <th className="px-4 py-2 font-medium">Locations</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={`${h.observed_at}-${i}`} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2 text-xs text-gray-500">{fmtDateTime(h.observed_at)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                    {h.has_data ? (h.pdc_total ?? '—') : '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {h.dealer_total ?? '—'}
                  </td>
                  <td className="px-4 py-2"><LocationChips locations={h.locations} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function Row({ field, value }: { field: string; value: unknown }) {
  return (
    <div className="flex flex-col py-1">
      <dt className="text-xs text-gray-400">{LABELS[field] ?? field}</dt>
      <dd className="text-sm text-gray-900">
        {field === 'ns_product_id' && typeof value === 'number' ? (
          <a
            href={`${NS_BASE_URL}${value}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {value}
          </a>
        ) : field === 'magento_id' && value != null ? (
          <a
            href={`${MAGENTO_BASE_URL}${value}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {String(value)}
          </a>
        ) : (
          formatValue(field, value)
        )}
      </dd>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col py-1">
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean')                            return value ? 'Yes' : 'No';
  if (PRICE_FIELDS.has(field) && typeof value === 'number') return fmtPrice(value);
  if (typeof value === 'number') return String(value);
  return String(value);
}
